import React, { HTMLAttributes, useEffect } from 'react';
import {
    AnchorProps,
    IconDataType,
    EmbeddedMessage,
    CoverImage,
    Title,
    Icon,
    Subtitle,
    CloseButton,
} from './EmbeddedMessage';

export interface IconInfo {
    iconData: string;
    iconDataType?: IconDataType;
}

export interface CustomClassNames {
    componentRoot?: string;
    textContainer?: string;
    closeButtonContainer?: string;
    textAndIconContainer?: string;
    messageAndImageContainer?: string;
    title?: string;
    icon?: string;
    subtitle?: string;
}

export interface EmbeddedMessageWrapperProps {
    title: string;
    subtitle: string;
    icon: IconInfo | Function;
    closeButtonAriaLabel: string;
    coverImageURL?: string;
    anchorProps?: AnchorProps;
    onCloseButtonClick?: (event: React.SyntheticEvent<EventTarget>) => void;
    onMessageClick?: (event: React.SyntheticEvent<EventTarget>) => void;
    onMount?: () => void;
    withoutBubble?: boolean;
    customClassNames?: CustomClassNames;
    rootElementProps?: HTMLAttributes<HTMLDivElement>;
    messageWrapperElementProps?: HTMLAttributes<HTMLDivElement>;
}

export const EmbeddedMessageWrapper = (props: EmbeddedMessageWrapperProps) => {
    const {
        title,
        coverImageURL,
        icon,
        subtitle,
        withoutBubble,
        customClassNames,
        anchorProps,
        onMount,
        onMessageClick,
        closeButtonAriaLabel,
        onCloseButtonClick,
        rootElementProps,
        messageWrapperElementProps,
    } = props;

    useEffect(() => {
        onMount?.();
    }, []);

    return (
        <div className={customClassNames.componentRoot} {...rootElementProps}>
            <EmbeddedMessage
                anchor={anchorProps}
                onClick={onMessageClick}
                messageWrapperElementProps={messageWrapperElementProps}
                withoutBubble={withoutBubble}
                textContainerCustomClassName={customClassNames.textContainer}
                closeButtonContainerCustomClassName={customClassNames.closeButtonContainer}
                textAndCoverImageContainerClassName={customClassNames.messageAndImageContainer}
                textIconContainerCustomClassName={customClassNames.textAndIconContainer}>
                {coverImageURL && <CoverImage imageURL={coverImageURL} type="CoverImage" />}
                <Title type="Title" customClassName={customClassNames.title}>
                    {title}
                </Title>
                {!icon ? null : isIconInfo(icon) ? (
                    <Icon
                        type="Icon"
                        customClassName={customClassNames.icon}
                        iconData={icon.iconData}
                        iconDataType={icon.iconDataType}
                    />
                ) : (
                    <Icon type="Icon" customClassName={customClassNames.icon}>
                        {icon()}
                    </Icon>
                )}
                <Subtitle type="Subtitle" customClassName={customClassNames.subtitle}>
                    {subtitle}
                </Subtitle>
                <CloseButton
                    type="CloseButton"
                    onClick={onCloseButtonClick}
                    ariaLabel={closeButtonAriaLabel}
                />
            </EmbeddedMessage>
        </div>
    );
};

EmbeddedMessageWrapper.defaultProps = {
    customClassNames: {},
};

const isIconInfo = (icon: IconInfo | Function): icon is IconInfo => typeof icon !== 'function';
